import { Fragment, useMemo, useState } from "react";
import { useLocation } from "wouter";
import { trpc } from "@/lib/trpc";
import { useAuth } from "@/contexts/AuthContext";

// ─── Recusas (Raz-only) ────────────────────────────────────────────────────────
// Tela unificada de recusas (peças + temas + pautas). Filtros + resumo + export.
// Pedido João ago/26.

const ORIGEM: Record<string, { label: string; cor: string; bg: string }> = {
  peca_copy: { label: "Peça · copy", cor: "#9333EA", bg: "#F3E8FF" },
  peca_design: { label: "Peça · design", cor: "#2563EB", bg: "#DBEAFE" },
  tema: { label: "Tema", cor: "#B45309", bg: "#FEF3C7" },
  pauta: { label: "Pauta desaprovada", cor: "#DC2626", bg: "#FEE2E2" },
};
const REDE: Record<string, string> = { instagram: "Instagram", linkedin: "LinkedIn", tiktok: "TikTok", youtube: "YouTube" };
const MESES = ["jan", "fev", "mar", "abr", "mai", "jun", "jul", "ago", "set", "out", "nov", "dez"];

function fmtData(ms: number): string {
  const d = new Date(ms + 3 * 3600000);
  return `${String(d.getUTCDate()).padStart(2, "0")}/${String(d.getUTCMonth() + 1).padStart(2, "0")}/${d.getUTCFullYear()}`;
}
function mesLabel(ms: number): string {
  const d = new Date(ms + 3 * 3600000);
  return `${MESES[d.getUTCMonth()]}/${d.getUTCFullYear()}`;
}
const dateToMs = (s: string, end = false) => {
  if (!s) return undefined;
  const [y, m, d] = s.split("-").map(Number);
  return Date.UTC(y, m - 1, d, end ? 23 : 0, end ? 59 : 0, end ? 59 : 0) + 3 * 3600000;
};

export default function Recusas() {
  const [, navigate] = useLocation();
  const { session } = useAuth();

  const [clientId, setClientId] = useState<number | undefined>(undefined);
  const [origem, setOrigem] = useState<string>("todas");
  const [autor, setAutor] = useState<string>("todos");
  const [rede, setRede] = useState<string>("todas");
  const [desde, setDesde] = useState("");
  const [ate, setAte] = useState("");
  const [q, setQ] = useState("");
  const [group, setGroup] = useState<"nenhum" | "card" | "autor" | "mes">("nenhum");

  const filtros = {
    clientId,
    origem: origem === "todas" ? undefined : (origem as any),
    autor: autor === "todos" ? undefined : autor,
    rede: rede === "todas" ? undefined : rede,
    desde: dateToMs(desde),
    ate: dateToMs(ate, true),
    q: q.trim() || undefined,
  };

  const { data: rows = [], isLoading } = trpc.recusas.list.useQuery(filtros);
  const { data: resumo } = trpc.recusas.resumo.useQuery(filtros);
  const { data: clientes = [] } = trpc.clients.list.useQuery(undefined, { staleTime: 5 * 60_000 });

  const autores = useMemo(
    () => [...new Set(rows.map(r => r.quemRecusou).filter(Boolean) as string[])].sort(),
    [rows]
  );

  const grupos = useMemo(() => {
    if (group === "nenhum") return [{ chave: "", linhas: rows }];
    const map = new Map<string, typeof rows>();
    for (const r of rows) {
      const k = group === "card" ? (r.codigo || `#${r.cardId}`)
        : group === "autor" ? (r.quemRecusou ?? "—")
        : mesLabel(r.dataRecusaMs);
      if (!map.has(k)) map.set(k, []);
      map.get(k)!.push(r);
    }
    return [...map.entries()]
      .sort((a, b) => b[1].length - a[1].length)
      .map(([chave, linhas]) => ({ chave, linhas }));
  }, [rows, group]);

  const exportar = async () => {
    const p = new URLSearchParams();
    if (filtros.clientId) p.set("clientId", String(filtros.clientId));
    if (filtros.origem) p.set("origem", filtros.origem);
    if (filtros.autor) p.set("autor", filtros.autor);
    if (filtros.rede) p.set("rede", filtros.rede);
    if (filtros.desde) p.set("desde", String(filtros.desde));
    if (filtros.ate) p.set("ate", String(filtros.ate));
    if (filtros.q) p.set("q", filtros.q);
    const resp = await fetch(`/api/export/recusas-csv?${p.toString()}`, {
      headers: { "x-client-token": session?.token ?? "" },
    });
    if (!resp.ok) { alert("Falha ao exportar."); return; }
    const blob = await resp.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = (resp.headers.get("content-disposition")?.match(/filename="(.+?)"/)?.[1]) || "recusas.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="h-full overflow-y-auto bg-[var(--solar-bg)]">
      <div className="max-w-[1400px] mx-auto px-6 py-6">
        <div className="flex items-baseline gap-3 mb-1">
          <h1 className="text-[var(--solar-ink)] font-bold text-2xl" style={{ fontFamily: "Poppins, sans-serif" }}>Recusas</h1>
          <span className="text-[var(--solar-mute)] text-sm">peças, temas e pautas recusadas · todos os clientes</span>
        </div>
        <p className="text-[var(--solar-mute)] text-[13px] mb-5">
          O que foi recusado, por quem e por quê. Insumo do banco de do's &amp; don'ts. Só Raz vê esta tela.
        </p>

        {/* Resumo */}
        {resumo && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
            <Resumo n={resumo.total} label="Recusas no período" />
            <Resumo n={resumo.taxaRecusa != null ? `${resumo.taxaRecusa}%` : "—"}
              label={`Taxa de recusa (${resumo.pecaRecusas}/${resumo.enviadasRevisao} peças)`} />
            <Resumo n={resumo.topAutores[0]?.[1] ?? 0}
              label={`Mais recusou: ${resumo.topAutores[0]?.[0] ?? "—"}`} />
            <Resumo n={resumo.recordista ? `${resumo.recordista.loops}×` : "—"}
              label={`Recordista de loops${resumo.recordista ? `: ${resumo.recordista.titulo.slice(0, 22)}` : ""}`} />
          </div>
        )}

        {/* Filtros */}
        <div className="flex flex-wrap gap-2 mb-3">
          <Sel value={clientId ? String(clientId) : "todos"} onChange={v => setClientId(v === "todos" ? undefined : Number(v))}
            opts={[["todos", "Todos os clientes"], ...clientes.map((c: any) => [String(c.id), c.nome] as [string, string])]} />
          <Sel value={origem} onChange={setOrigem}
            opts={[["todas", "Todas as origens"], ["peca_copy", "Peça · copy"], ["peca_design", "Peça · design"], ["tema", "Tema"], ["pauta", "Pauta desaprovada"]]} />
          <Sel value={autor} onChange={setAutor} opts={[["todos", "Quem recusou"], ...autores.map(a => [a, a] as [string, string])]} />
          <Sel value={rede} onChange={setRede} opts={[["todas", "Todas as redes"], ["instagram", "Instagram"], ["linkedin", "LinkedIn"], ["tiktok", "TikTok"]]} />
          <input type="date" value={desde} onChange={e => setDesde(e.target.value)} className="text-[13px] bg-white border border-[var(--solar-line)] rounded-lg px-2 py-1.5 text-[var(--ink-2)]" title="De" />
          <input type="date" value={ate} onChange={e => setAte(e.target.value)} className="text-[13px] bg-white border border-[var(--solar-line)] rounded-lg px-2 py-1.5 text-[var(--ink-2)]" title="Até" />
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Buscar no motivo/título…"
            className="text-[13px] bg-white border border-[var(--solar-line)] rounded-lg px-3 py-1.5 text-[var(--ink-2)] flex-1 min-w-[180px] focus:outline-none focus:border-[var(--brand)]" />
        </div>

        {/* Agrupar + export */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2 text-[13px]">
            <span className="text-[var(--solar-mute)]">Agrupar:</span>
            {(["nenhum", "card", "autor", "mes"] as const).map(g => (
              <button key={g} onClick={() => setGroup(g)}
                className={`px-2.5 py-1 rounded-lg ${group === g ? "bg-[var(--solar-ink)] text-white" : "bg-white text-[var(--solar-mute)] border border-[var(--solar-line)]"}`}>
                {g === "nenhum" ? "Nenhum" : g === "card" ? "Por card" : g === "autor" ? "Por autor" : "Por mês"}
              </button>
            ))}
          </div>
          <button onClick={exportar} className="text-[13px] bg-white border border-[var(--solar-line)] rounded-lg px-3 py-1.5 text-[var(--solar-ink)] font-medium hover:bg-[var(--solar-bg)]">
            ⬇ Exportar CSV
          </button>
        </div>

        {/* Tabela */}
        <div className="bg-white rounded-xl border border-[var(--solar-line)] shadow-[var(--elev-1)] overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead>
                <tr className="text-left text-[var(--solar-mute)] border-b border-[var(--solar-line)]" style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: ".04em" }}>
                  <th className="py-2.5 pl-4 pr-2 font-semibold">Data</th>
                  <th className="py-2.5 px-2 font-semibold">Cliente</th>
                  <th className="py-2.5 px-2 font-semibold">Origem</th>
                  <th className="py-2.5 px-2 font-semibold">Título</th>
                  <th className="py-2.5 px-2 font-semibold">Rede</th>
                  <th className="py-2.5 px-2 font-semibold">Quem recusou</th>
                  <th className="py-2.5 px-2 font-semibold">Motivo</th>
                  <th className="py-2.5 px-2 font-semibold text-center">Nº</th>
                  <th className="py-2.5 px-2 font-semibold">Status</th>
                </tr>
              </thead>
              <tbody>
                {isLoading && <tr><td colSpan={9} className="py-8 text-center text-[var(--solar-mute)]">Carregando…</td></tr>}
                {!isLoading && rows.length === 0 && <tr><td colSpan={9} className="py-8 text-center text-[var(--solar-mute)]">Nenhuma recusa no filtro.</td></tr>}
                {grupos.map(({ chave, linhas }) => (
                  <Fragment key={chave || "all"}>
                    {chave && (
                      <tr className="bg-[var(--solar-bg)]">
                        <td colSpan={9} className="py-2 px-4 text-[12px] font-semibold text-[var(--solar-ink)]">
                          {chave} <span className="text-[var(--solar-mute)] font-normal">· {linhas.length}</span>
                        </td>
                      </tr>
                    )}
                    {linhas.map((r, i) => {
                      const o = ORIGEM[r.origem];
                      return (
                        <tr key={`${r.origem}-${r.cardId ?? "x"}-${r.dataRecusaMs}-${i}`}
                          onClick={() => r.link && navigate(r.link)}
                          className={`border-b border-[var(--solar-line)] ${r.link ? "hover:bg-[var(--solar-bg)] cursor-pointer" : ""}`}>
                          <td className="py-2.5 pl-4 pr-2 text-[var(--solar-mute)] tabular-nums whitespace-nowrap">{fmtData(r.dataRecusaMs)}</td>
                          <td className="py-2.5 px-2 text-[var(--ink-2)] whitespace-nowrap">{r.clientNome}</td>
                          <td className="py-2.5 px-2 whitespace-nowrap">
                            <span className="text-[11px] px-1.5 py-0.5 rounded-full font-medium" style={{ background: o?.bg, color: o?.cor }}>{o?.label}</span>
                          </td>
                          <td className="py-2.5 px-2 text-[var(--solar-ink)] font-medium max-w-[260px] truncate">
                            {r.titulo}
                            {r.codigo && <span className="ml-2 text-[11px] text-[var(--solar-mute)] font-mono">{r.codigo}</span>}
                          </td>
                          <td className="py-2.5 px-2 text-[var(--solar-mute)] whitespace-nowrap">{r.rede ? (REDE[r.rede] ?? r.rede) : "—"}</td>
                          <td className="py-2.5 px-2 text-[var(--solar-mute)] whitespace-nowrap">{r.quemRecusou ?? "—"}</td>
                          <td className="py-2.5 px-2 text-[var(--solar-mute)] max-w-[280px] truncate" title={r.motivo ?? ""}>{r.motivo ?? "—"}</td>
                          <td className="py-2.5 px-2 text-center tabular-nums text-[var(--ink-2)]">{r.ordinal}ª</td>
                          <td className="py-2.5 px-2 text-[var(--solar-mute)] whitespace-nowrap">{r.statusAtual}</td>
                        </tr>
                      );
                    })}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <p className="text-[11px] text-[var(--solar-mute)] mt-3">
          F1: motivo de peça é heurístico (comentário mais próximo da recusa). Captura obrigatória de motivo + categoria vem na F2.
        </p>
      </div>
    </div>
  );
}

function Resumo({ n, label }: { n: number | string; label: string }) {
  return (
    <div className="bg-white rounded-xl border border-[var(--solar-line)] shadow-[var(--elev-1)] px-4 py-3">
      <div className="text-2xl font-bold tabular-nums text-[var(--solar-ink)]" style={{ fontFamily: "Poppins, sans-serif" }}>{n}</div>
      <div className="text-[11px] text-[var(--solar-mute)] mt-0.5 truncate" title={label}>{label}</div>
    </div>
  );
}

function Sel({ value, onChange, opts }: { value: string; onChange: (v: string) => void; opts: [string, string][] }) {
  return (
    <select value={value} onChange={e => onChange(e.target.value)}
      className="text-[13px] bg-white border border-[var(--solar-line)] rounded-lg px-3 py-1.5 text-[var(--ink-2)] focus:outline-none focus:border-[var(--brand)]">
      {opts.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
    </select>
  );
}
